/**
 * wedding.js
 * -----------------------------------------------------------
 * Single source of truth for ALL wedding content.
 *  - Edit this file to change any text on the site.
 *  - Drop images at the listed paths inside /public (e.g. public/story/1.jpg).
 *    Missing images show an elegant placeholder automatically.
 *  - Search for "____" to find placeholders that still need real content.
 *  - See IMAGES.md for recommended image sizes.
 */
export const COUPLE_PHOTO = { src: "/portraits/couple.jpg", alt: "Sonakshi and Shubham at Hawa Mahal, Jaipur" };

export const COUPLE = {
  groom: {
    name: "Shubham",
    full: "Shubham Raj",
    parents: "Mrs. Seema Sinha & Mr. Kundan Kumar Sinha",
    // Shown under "Grandson of"; `note` is an optional small second line
    grandparents: [
      { relation: "Dadi ji", name: "Smt. Sheela Sinha" },
      { relation: "Dada ji", name: "Late Shri Rajeshwar Prasad Sinha" },
      { relation: "Nani ji", name: "Late Smt. Janki Sharan" },
      { relation: "Nana ji", name: "Late Shri Sheo Chandra Prasad" },
    ],
    city: "Patna",
    photo: "/portraits/groom.jpg",
  },
  bride: {
    name: "Sonakshi",
    full: "Sonakshi Srivastava",
    parents: "Mrs. Smita Srivastava & Mr. Hirendra Kumar Srivastava",
    // Shown under "Granddaughter of"
    grandparents: [
      { relation: "Dadi ji", name: "Late Smt. Ram Rati Devi" },
      { relation: "Dada ji", name: "Late Shri H.P. Srivastava" },
      { relation: "Nani ji", name: "Late Smt. Uma Srivastava" },
      { relation: "Nana ji", name: "Shri M.M. Srivastava" },
    ],
    city: "Kanpur",
    photo: "/portraits/bride.jpg",
  },
};

export const WEDDING = {
  dateLabel: "20 & 21 November 2026",
  muhuratLabel: "20 & 21 November",
  countdownTarget: "2026-11-22T00:00:00+05:30", // Phere at midnight (IST)
  venue: {
    name: "Riverarch Greenfields Resorts",
    area: "Jaipur, Rajasthan, India",
    address: "Jaipur, Rajasthan",
    mapEmbedUrl: "https://www.google.com/maps?q=27.1733702,75.9982933&z=14&output=embed",
    mapUrl: "https://maps.app.goo.gl/gspngvsXVsvtbEwc7",
    website: "http://riverarch.in/",
    image: "/venue/venue.jpg",
    notes: [], // optional extra lines shown under the photo
    // Shown as a highlighted card in the Venue section
    stayNote: "Your stay is arranged for you at the venue itself. Rooms for all our guests are taken care of, so just bring yourselves.",
  },
};

// Ceremony cards (Ceremonies section + Shubh Muhurat screen): `blurb` is the one or two line description.
export const EVENTS = [
  { key: "arrival", name: "Arrival", short: "Welcome", hindi: "शुभ आगमन", date: "20 Nov", time: "11:00 AM", venue: "Riverarch Greenfields Resorts, Jaipur", blurb: "Welcome to Jaipur! Settle in, meet the families and let the celebrations begin." },
  { key: "sangeet", name: "Sangeet, Cocktails & Dinner", short: "Sangeet", hindi: "संगीत", date: "20 Nov", time: "6:30 PM", venue: "Riverarch Greenfields Resorts, Jaipur", blurb: "An evening of music, dance and laughter as both families take the stage." },
  { key: "haldi", name: "Phoolon ki Haldi", short: "Haldi", hindi: "हल्दी", date: "21 Nov", time: "11:00 AM", venue: "Riverarch Greenfields Resorts, Jaipur", blurb: "A sunny morning of haldi and showers of fresh flower petals, with blessings for the couple. Do wear yellow if you can!" },
  { key: "baraat", name: "Baraat, Jaimala & Dinner", short: "Baraat & Jaimala", hindi: "बारात एवं जयमाला", date: "21 Nov", time: "6:30 PM", venue: "Riverarch Greenfields Resorts, Jaipur", blurb: "The groom arrives to dhol beats and dancing, followed by the exchange of garlands." },
  { key: "phere", name: "Phere", hindi: "फेरे", date: "21 Nov", time: "12:00 AM (Midnight)", shortTime: "Midnight", venue: "Riverarch Greenfields Resorts, Jaipur", blurb: "Seven sacred rounds around the holy fire, as two families become one." },
  { key: "vidaai", name: "Vidaai", hindi: "विदाई", date: "22 Nov", time: "10:00 AM", venue: "Riverarch Greenfields Resorts, Jaipur", blurb: "A tender farewell full of blessings as Sonakshi  begins her new journey." },
];

// Shown on the varmala "Shubh Muhurat" screen
export const HIGHLIGHT_EVENTS = ["arrival", "sangeet", "haldi", "baraat", "phere"];

// Full day-by-day programme, including meals
export const SCHEDULE = [
  { day: "20 November 2026", weekday: "Friday", items: [
    { time: "11:00 AM", name: "Arrival" },
    { time: "1:00 PM", name: "Lunch" },
    { time: "6:30 PM", name: "Sangeet, Cocktails & Dinner" },
  ] },
  { day: "21 November 2026", weekday: "Saturday", items: [
    { time: "8:30 AM", name: "Breakfast" },
    { time: "11:00 AM", name: "Haldi" },
    { time: "1:30 PM", name: "Lunch" },
    { time: "6:30 PM", name: "Baraat, Jaimala & Dinner" },
    { time: "12:00 AM (Midnight)", name: "Phere" },
  ] },
  { day: "22 November 2026", weekday: "Sunday", items: [
    { time: "10:00 AM", name: "Vidaai" },
  ] },
];

// RSVP contact shown in the RSVP section
export const RSVP_CONTACT = {
  name: "Mrs. Sheela Sinha & Family",
  phones: ["+91-9835290931", "+91-7979089775", "+91-9334144072"],
};

export const FAMILIES = {
  swagatotsuk: [
    { name: "Mrs. Smita Srivastava & Mr. Hirendra Kumar Srivastava", role: "Parents of the bride" },
  ],
  darshanabhilashi: ["Kins and friends of both families"],
};

// Our Journey timeline. `image` shows beside the chapter; `aspect` keeps its shape (portrait 3/4, landscape 3/2).
export const STORY = [
  {
    date: "October 2025",
    title: "Where It All Began",
    image: "/story/1.jpg",
    aspect: "4/5",
    quote: "Autumn is all about falling: falling leaves, falling temperatures, and falling in love.",
    text: "Some stories begin with a hello. Ours began with two families meeting. On 19 October, our families met, and so did we. Six days later, on 25 October, we met just the two of us. It was supposed to be a short conversation. We never noticed where the time went.",
  },
  {
    date: "November to December 2025",
    title: "The Months It All Became Real",
    image: "/story/2.jpg",
    aspect: "3/4",
    quote: "Distance only made the conversations longer.",
    text: "Phone calls became daily, and daily became hourly. Our families met again, we visited each other’s homes, and we promised ourselves to each other, ready to take the sacred pheras together.",
  },
  {
    date: "January 2026",
    title: "Officially Engaged",
    image: "/story/3.jpg",
    aspect: "3/2",
    quote: "A ring, a promise, and a lot of happy tears.",
    text: "On 16 January, with our families around us, we got engaged. Two weeks later came Sona’s birthday, celebrated across the miles, the last one we’ll ever spend apart.",
  },
  {
    date: "February to May 2026",
    title: "Miles Apart, Never Far",
    image: "/story/4.jpg",
    aspect: "3/4",
    quote: "Tokyo to Delhi is a long way. Love made it feel short.",
    text: "Different countries, different time zones, and calls that ran till 3 am. On 14 April, after 12 long weeks, we finally met again at Delhi airport, and airports have meant hellos and goodbyes ever since.",
  },
  {
    date: "August to September 2026",
    title: "Finding Our Home",
    image: "/story/5.jpg",
    aspect: "3/2",
    quote: "Home isn’t a place. But finding one together felt like magic.",
    text: "With Sona soon to join him, Shubh wanted a bigger home, one made for the two of them. So we started searching together for a place that suited us both, and in August, we found it. In September, Shubh flew back for the wedding preparations. After the longest wait of all, Sona was there at the airport with the warmest hug.",
  },
  {
    date: "November 2026",
    title: "And Now, Forever Begins",
    image: "/story/6.jpg",
    aspect: "3/2",
    quote: "One year after we first met, we’re ready for a lifetime.",
    text: "On 19 October, we completed one year since our first meeting. Now we invite you to Jaipur to celebrate the next chapter with us.",
  },
  {
    date: "The Next Chapter",
    title: "Written With You",
    image: "/story/7.jpg",
    aspect: "3/2",
    quote: "Every love story is beautiful, but ours is our favourite.",
    text: "One year, two cities, two countries, two time zones, and now, one. This is only the beginning. The rest of our story, we’d love to write with you in it.",
  },
];

// Shown under the Journey heading
export const STORY_INTRO = "Ours began as an arranged match, but it never felt like one. Somewhere between the first hello and the countless calls, it simply became love.";

// Closing lines after the last chapter
export const STORY_OUTRO = ["To be continued, forever.", "The best part? We’re just getting started."];

